import * as XLSX from 'xlsx';
import { ExcelTemplateConfig } from 'src/utils/excel/excelGridConfig';

const getDateString = (): string => {
	const today = new Date();
	return `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
};

export const buildHeaderKeyMap = (headerNames: Record<string, string>): Record<string, string> => {
	return Object.entries(headerNames).reduce((acc, [key, value]) => {
		if (value) {
			acc[value] = key;
		}
		return acc;
	}, {} as Record<string, string>);
};

/**
 * 첫 번째 워크시트를 객체 배열로 변환한다.
 * maxColumnCount가 있으면 해당 열까지만 읽어 시트 우측의 안내영역을 업로드 데이터에서 제외한다.
 */
export const parseExcelFile = (
	file: File,
	maxColumnCount?: number
): Promise<Record<string, unknown>[]> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const data = e.target?.result;
				const workbook = XLSX.read(data, { type: 'array' });
				const sheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[sheetName];
				let range: string | undefined;
				if (maxColumnCount && maxColumnCount > 0 && worksheet['!ref']) {
					// 실제 사용 영역의 마지막 행은 유지하고 마지막 열만 설정된 최대 열로 제한한다.
					const worksheetRange = XLSX.utils.decode_range(worksheet['!ref']);
					worksheetRange.e.c = Math.min(worksheetRange.e.c, maxColumnCount - 1);
					range = XLSX.utils.encode_range(worksheetRange);
				}
				const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(
					worksheet,
					range ? { range } : undefined
				);
				resolve(json);
			} catch (error) {
				reject(error);
			}
		};
		reader.onerror = () => reject(new Error('파일 읽기 실패'));
		reader.readAsArrayBuffer(file);
	});
};

export const mapRowFromExcel = <T extends object>(
	row: Record<string, unknown>,
	headerKeyMap: Record<string, string>,
	defaultRow: T,
	valueTransformers?: Partial<Record<string, (value: unknown) => unknown>>
): T => {
	const newRow: Record<string, unknown> = { ...(defaultRow as Record<string, unknown>) };

	for (const excelHeader in row) {
		const dtoKey = headerKeyMap[excelHeader];
		if (!dtoKey) continue;

		let value = row[excelHeader];
		const transformer = valueTransformers?.[dtoKey];
		if (transformer) {
			value = transformer(value);
		}
		newRow[dtoKey] = value;
	}

	return newRow as T;
};

export const validateRequiredHeaders = (
	excelHeaders: string[],
	requiredHeaders: string[]
): string[] => {
	return requiredHeaders.filter((header) => !excelHeaders.includes(header));
};

export const getNextValueIndexes = (
	existingList: { valueIndex?: number | string }[],
	count: number
): number[] => {
	const base = Math.max(0, ...existingList.map((item) => Number(item.valueIndex) || 0));
	return Array.from({ length: count }, (_, index) => base + index + 1);
};

export const downloadExcelTemplate = (config: ExcelTemplateConfig): void => {
	const {
		columnOrder,
		headerNames,
		colWidths,
		sheetName = '템플릿',
		fileNamePrefix = '업로드_템플릿',
		sampleData = [],
		sideGuide,
	} = config;

	const headers = columnOrder.map((field) => headerNames[field]);
	const wb = XLSX.utils.book_new();
	const data = sampleData.length > 0 ? [headers, ...sampleData] : [headers];
	const ws = XLSX.utils.aoa_to_sheet(data);

	if (sideGuide) {
		// 제목·설명·헤더·본문을 하나의 2차원 배열로 구성해 지정한 시작 열에 추가한다.
		const guideData = [
			[sideGuide.title],
			...(sideGuide.description ? [[sideGuide.description]] : []),
			sideGuide.headers,
			...sideGuide.rows,
		];
		XLSX.utils.sheet_add_aoa(ws, guideData, {
			origin: { r: 0, c: sideGuide.startColumn },
		});
	}

	if ((colWidths && colWidths.length > 0) || sideGuide?.colWidths?.length) {
		// 데이터 영역과 안내영역의 열 너비를 동일한 !cols 배열에 병합한다.
		const worksheetColumns: XLSX.ColInfo[] = [];
		colWidths?.forEach((width, index) => {
			worksheetColumns[index] = { wch: width };
		});
		if (sideGuide) {
			// 안내표 바로 앞 열이 비어 있으면 좁은 구분 열로 설정해 두 영역을 시각적으로 분리한다.
			if (sideGuide.startColumn > 0 && !worksheetColumns[sideGuide.startColumn - 1]) {
				worksheetColumns[sideGuide.startColumn - 1] = { wch: 3 };
			}
			sideGuide.colWidths?.forEach((width, index) => {
				worksheetColumns[sideGuide.startColumn + index] = { wch: width };
			});
		}
		ws['!cols'] = worksheetColumns;
	}

	XLSX.utils.book_append_sheet(wb, ws, sheetName);
	XLSX.writeFile(wb, `${fileNamePrefix}_${getDateString()}.xlsx`);
};
